import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';
import { LocationProvider } from './context/LocationContext';
import ProtectedRoute from './components/ProtectedRoute';
import { clearAppCache } from './lib/storage';
import Navigation from './components/Navigation';
import AgeGate from './components/AgeGate';
import TermsGate from './components/TermsGate';
import BadgeCelebration from './components/BadgeCelebration';
import { useBadgeCelebration } from './hooks/useBadgeCelebration';
import MapView from './pages/MapView';
import ListView from './pages/ListView';
import BarDetail from './pages/BarDetail';
import AddBar from './pages/AddBar';
import Profile from './pages/Profile';
import Friends from './pages/Friends';
import FriendProfile from './pages/FriendProfile';
import Connections from './pages/Connections';
import MyLists from './pages/MyLists';
import ListDetail from './pages/ListDetail';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import AdminDashboard from './pages/AdminDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ApproveBarRequest from './pages/ApproveBarRequest';
import ActivityFeed from './pages/ActivityFeed';
import ScanPage from './pages/ScanPage';
import Settings from './pages/Settings';
import AboutUs from './pages/AboutUs';
import Feedback from './pages/Feedback';
import SharePage from './pages/SharePage';
import ResetPassword from './pages/ResetPassword';
import SmartAppBanner from './components/SmartAppBanner';
import HometownPrompt from './components/HometownPrompt';
import { useAuth } from './context/AuthContext';
import supabase from './lib/supabase';
import { setupUniversalLinkHandler } from './lib/universalLinks';

// Generate or retrieve a unique visitor ID for anonymous tracking
const getVisitorId = () => {
  let visitorId = localStorage.getItem('tilted_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    try {
      localStorage.setItem('tilted_visitor_id', visitorId);
    } catch (e) {
      // localStorage might be unavailable in private browsing
    }
  }
  return visitorId;
};

// Capture referral code from URL (e.g., ?ref=ABC123)
const captureReferralCode = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');
    
    if (referralCode) {
      sessionStorage.setItem('tilted_referral', referralCode);
      console.log('Referral code captured:', referralCode);
      
      // Clean up URL without refreshing
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
    }
  } catch (e) {
    console.error('Error capturing referral code:', e);
  }
};

// Capture referral on initial load
captureReferralCode();

// Simple hook to reset scroll on route changes
function useScrollReset() {
  const location = useLocation();
  
  // Reset scroll on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
}

// Hook to track page visits for analytics
function usePageTracking() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page visit (runs on every route change)
    const trackVisit = async () => {
      try {
        const visitorId = getVisitorId();
        await supabase.from('page_visits').insert({
          visitor_id: visitorId,
          page_path: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent
        });
      } catch (error) {
        // Silently fail - don't break the app for tracking
        console.debug('Page tracking failed:', error.message);
      }
    };
    
    trackVisit();
  }, [location.pathname]);
}

// AgeGate wrapper - skip on deep links (share, bar, auth pages)
function AgeGateWrapper() {
  const location = useLocation();
  const path = location.pathname;
  if (path.startsWith('/share/') || path.startsWith('/bar/') || path.startsWith('/auth') || path === '/reset-password') return null;
  return <AgeGate />;
}

// TermsGate wrapper - allow reading legal pages while gate is shown
function TermsGateWrapper() {
  const location = useLocation();
  const path = location.pathname;
  if (path === '/terms' || path === '/privacy' || path === '/reset-password') return null;
  return <TermsGate />;
}

// Section error boundary - isolates crashes to show which section failed
class SectionBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorDetail: '', autoRetried: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error(`[${this.props.name}] crashed:`, error, info);
    const stack = (info?.componentStack || '').split('\n').filter(l => l.trim()).slice(0, 5).join('\n');
    this.setState({ errorDetail: stack });

    // Auto-retry once after a short delay (fixes first-render timing crashes)
    if (!this.state.autoRetried) {
      setTimeout(() => {
        this.setState({ hasError: false, error: null, errorDetail: '', autoRetried: true });
      }, 150);
    }
  }
  render() {
    if (this.state.hasError) {
      // During auto-retry, show a loading spinner instead of the error
      if (!this.state.autoRetried) {
        return (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-dive-neon"></div>
          </div>
        );
      }
      // If auto-retry already happened and it still crashed, show the error
      return (
        <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 m-4 text-center">
          <p className="text-red-400 font-bold text-sm">{this.props.name} crashed</p>
          <p className="text-red-300 text-xs mt-1 break-all">{this.state.error?.message || 'Unknown'}</p>
          {this.state.errorDetail && (
            <pre className="text-red-300/70 text-[10px] mt-2 text-left whitespace-pre-wrap break-all bg-red-900/20 p-2 rounded">{this.state.errorDetail}</pre>
          )}
          <button onClick={() => this.setState({ hasError: false, error: null, errorDetail: '', autoRetried: false })} className="text-dive-neon text-xs underline mt-2">Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Layout wrapper that shows navigation for authenticated routes
function AppLayout({ children }) {
  return (
    <div className="min-h-screen bg-dive-black relative gritty">
      <SectionBoundary name="Navigation">
        <Navigation />
      </SectionBoundary>
      {/* Opaque strip behind the iPad status bar so scrolled content doesn't show through (mobile top nav covers it on phones) */}
      <div
        aria-hidden="true"
        className="hidden md:block fixed top-0 left-0 right-0 z-[1500] bg-dive-black pointer-events-none"
        style={{ height: 'env(safe-area-inset-top, 0px)' }}
      />
      {/* Top padding comes from --app-top-offset in index.css; md:pl-20 for desktop sidebar */}
      <main className="md:pl-20 pb-safe">
        <SectionBoundary name="PageContent">
          {children}
        </SectionBoundary>
      </main>
    </div>
  );
}

// Badge Celebration Manager - shows celebration popups for newly earned badges
function BadgeCelebrationManager({ userId }) {
  const { currentBadge, dismissCelebration } = useBadgeCelebration(userId);
  
  if (!currentBadge) return null;
  
  return (
    <BadgeCelebration 
      badge={currentBadge} 
      onClose={dismissCelebration} 
    />
  );
}

// Error banner for mobile debugging - shows when data fails to load
function DataErrorBanner() {
  const { loading, error, bars, refreshData } = useApp();
  const [dismissed, setDismissed] = React.useState(false);
  
  const handleClearCache = () => {
    if (confirm('This will clear cached data and reload. You\'ll stay logged in. Continue?')) {
      clearAppCache();
    }
  };
  
  if (dismissed) return null;
  
  // Show error if there's an error
  if (error) {
    return (
      <div className="fixed top-14 md:top-0 left-0 right-0 bg-red-900/95 text-white p-3 z-[100] md:ml-20">
        <div className="flex flex-col gap-2 max-w-3xl mx-auto">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="text-sm font-medium">⚠️ Error loading data</p>
              <p className="text-xs opacity-80 mt-1">{error}</p>
            </div>
            <button 
              onClick={() => setDismissed(true)}
              className="text-xs opacity-70 p-1"
            >
              ✕
            </button>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => { refreshData(); }}
              className="text-xs bg-white/20 px-3 py-1.5 rounded"
            >
              Retry
            </button>
            <button 
              onClick={handleClearCache}
              className="text-xs bg-red-600 px-3 py-1.5 rounded"
            >
              Clear Cache
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Show warning if no bars loaded after loading complete
  if (!loading && bars.length === 0) {
    return (
      <div className="fixed top-14 md:top-0 left-0 right-0 bg-amber-800/95 text-white p-3 z-[100] md:ml-20">
        <div className="flex flex-col gap-2 max-w-3xl mx-auto">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="text-sm font-medium">⚠️ No data loaded</p>
              <p className="text-xs opacity-80 mt-1">This may be a connection or cache issue.</p>
            </div>
            <button 
              onClick={() => setDismissed(true)}
              className="text-xs opacity-70 p-1"
            >
              ✕
            </button>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => window.location.reload()}
              className="text-xs bg-white/20 px-3 py-1.5 rounded"
            >
              Refresh
            </button>
            <button 
              onClick={handleClearCache}
              className="text-xs bg-amber-600 px-3 py-1.5 rounded"
            >
              Clear Cache
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
}


// Inner app with routes that need AppProvider
function AppRoutes() {
  const { loading: authLoading, user, profile, isNewUser } = useAuth();
  const [timeout, setShowTimeout] = React.useState(false);
  const navigate = useNavigate();

  // Handle Universal Links on warm start (app already running)
  // Cold start is handled in main.jsx before React renders
  useEffect(() => {
    setupUniversalLinkHandler(navigate);
  }, [navigate]);

  // Track page visits for analytics
  usePageTracking();

  // Simple scroll reset on route changes
  useScrollReset();

  // Show timeout message after 5 seconds of auth loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (authLoading) setShowTimeout(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [authLoading]);

  // Mount AppProvider immediately so it can start fetching data (bars, etc.)
  // in parallel with auth. Only show a blocking loader if auth is still
  // loading AND we have no cached data to show.
  return (
    <LocationProvider>
    <AppProvider>
      {/* Show auth loading screen only if auth hasn't resolved yet */}
      {authLoading ? (
        <div className="min-h-screen bg-dive-black flex flex-col items-center justify-center gap-4">
          <div className="text-dive-neon animate-pulse text-xl">Loading...</div>
          {timeout && (
            <div className="text-center">
              <p className="text-gray-400 text-sm mb-2">Taking longer than expected...</p>
              <button
                onClick={() => window.location.reload()}
                className="text-dive-neon underline text-sm"
              >
                Click to refresh
              </button>
            </div>
          )}
        </div>
      ) : (
      <>
      {/* Smart app banner: iOS → deep link / App Store, Android → PWA install */}
      <SmartAppBanner />

      {/* Age Gate - skip for signed-in users (verified at signup) */}
      {!user && <AgeGateWrapper />}

      {/* TOS Gate - shown after OAuth/login for users who haven't accepted */}
      {user && profile && profile.tos_accepted_at === null && <TermsGateWrapper />}

      {/* Badge Celebration Manager - shows celebration popups for new badges */}
      {user && <BadgeCelebrationManager userId={user.id} />}
      {user && <HometownPrompt />}

      {/* Mobile data loading error banner */}
      <DataErrorBanner />

      <Routes>
        {/* Public Auth Routes */}
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route
          path="/about"
          element={
            <AppLayout>
              <AboutUs />
            </AppLayout>
          }
        />
        <Route path="/feedback" element={<Feedback />} />
        <Route
          path="/settings"
          element={
            <AppLayout>
              <Settings />
            </AppLayout>
          }
        />
        <Route path="/approve-bar-request" element={<ApproveBarRequest />} />
        <Route path="/scan" element={<ScanPage />} />
        <Route path="/share/:ratingId" element={<SharePage />} />

        {/* Public Routes with Navigation */}
        <Route
          path="/"
          element={
            <AppLayout>
              <MapView />
            </AppLayout>
          }
        />
        <Route
          path="/list"
          element={
            <AppLayout>
              <ListView />
            </AppLayout>
          }
        />
        <Route
          path="/bar/:id"
          element={
            <AppLayout>
              <BarDetail />
            </AppLayout>
          }
        />
        
        {/* Routes with built-in auth prompts */}
        <Route
          path="/add"
          element={
            <AppLayout>
              <AddBar />
            </AppLayout>
          }
        />
        <Route
          path="/profile"
          element={
            <AppLayout>
              <Profile />
            </AppLayout>
          }
        />
        <Route
          path="/social"
          element={
            <AppLayout>
              <Friends />
            </AppLayout>
          }
        />
        <Route
          path="/social/:userId"
          element={
            <AppLayout>
              <ProtectedRoute>
                <FriendProfile />
              </ProtectedRoute>
            </AppLayout>
          }
        />
        {/* Connections page (followers/following lists) */}
        <Route
          path="/connections"
          element={
            <AppLayout>
              <ProtectedRoute>
                <Connections />
              </ProtectedRoute>
            </AppLayout>
          }
        />
        {/* Legacy routes - redirect to new paths */}
        <Route path="/friends" element={<Navigate to="/social" replace />} />
        <Route path="/friends/:userId" element={<Navigate to="/social/:userId" replace />} />
        <Route
          path="/activity"
          element={
            <AppLayout>
              <ProtectedRoute>
                <ActivityFeed />
              </ProtectedRoute>
            </AppLayout>
          }
        />
        <Route
          path="/lists"
          element={
            <AppLayout>
              <MyLists />
            </AppLayout>
          }
        />
        <Route
          path="/lists/:listId"
          element={
            <AppLayout>
              <ProtectedRoute>
                <ListDetail />
              </ProtectedRoute>
            </AppLayout>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
      </>
      )}
    </AppProvider>
    </LocationProvider>
  );
}

// Error boundary to catch React crashes and show recovery UI instead of black screen
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '', componentStack: '' };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Unknown error' };
  }
  componentDidCatch(error, info) {
    console.error('App crash:', error, info);
    // Save component stack so we can see which component crashed
    this.setState({ componentStack: info?.componentStack || '' });
  }
  render() {
    if (this.state.hasError) {
      // Extract the first few lines of component stack to show which component crashed
      const stackLines = this.state.componentStack
        ? this.state.componentStack.split('\n').filter(l => l.trim()).slice(0, 8).join('\n')
        : 'No stack available';
      return (
        <div className="min-h-screen bg-dive-black flex flex-col items-center justify-center gap-4 p-4">
          <img src="/logo-full.png" alt="Tilted" className="w-48 opacity-50" />
          <p className="text-gray-400">Something went wrong.</p>
          <p className="text-gray-600 text-xs max-w-sm text-center break-all">{this.state.errorMessage}</p>
          <pre className="text-gray-700 text-[10px] max-w-sm text-left overflow-x-auto whitespace-pre-wrap break-all mt-2 bg-gray-900 p-2 rounded">{stackLines}</pre>
          <button
            onClick={() => { this.setState({ hasError: false, errorMessage: '', componentStack: '' }); window.location.href = '/'; }}
            className="text-dive-neon underline"
          >
            Go Home
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
      <Analytics />
    </ErrorBoundary>
  );
}

export default App;
